import React, { useState } from 'react';
import { UserPlusIcon, EnvelopeIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { mockBusinessStaff, mockCharityStaff, mockBusinessInvitations, mockCharityInvitations } from '@/utils/mockData';
import type { BusinessUser, Invitation } from '@/types/userManagement';
import type { BusinessRole, CharityRole } from '@/types/roles';
import { toast } from 'react-hot-toast';

// This component is for development mode only and does not use any session or permission checks

interface DevStaffManagementProps {
  orgType: 'business' | 'charity';
}

type BusinessUserRole = 'staff' | 'supervisor' | 'manager' | 'owner';
type CharityUserRole = 'volunteer' | 'staff' | 'supervisor' | 'manager' | 'owner';

const DevStaffManagement: React.FC<DevStaffManagementProps> = ({ orgType }) => {
  // Use mock data based on organization type
  const [staff, setStaff] = useState<BusinessUser[]>(
    orgType === 'business' ? [...mockBusinessStaff] : [...mockCharityStaff]
  );
  const [invitations, setInvitations] = useState<Invitation[]>(
    orgType === 'business' ? [...mockBusinessInvitations] : [...mockCharityInvitations]
  );
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [newUser, setNewUser] = useState<Omit<BusinessUser, 'id' | 'status' | 'createdAt' | 'businessId'> & { role: BusinessUserRole | CharityUserRole }>({
    name: '',
    email: '',
    role: orgType === 'business' ? 'staff' : 'volunteer',
    profilePicture: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const sendInvitation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.email || !newUser.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSendingInvite(true);
      
      // Create a mock invitation
      const mockInvitation: Invitation = {
        id: `inv-${Date.now()}`,
        email: newUser.email,
        role: newUser.role as BusinessRole | CharityRole,
        businessId: orgType === 'business' ? '8c3e08e8-f9b0-4cd8-a55a-49435583f001' : '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
        status: 'pending'
      };
      
      // Add the invitation to the local state
      setInvitations(prev => [...prev, mockInvitation]);
      
      // Reset the form
      setNewUser({
        name: '',
        email: '',
        role: orgType === 'business' ? 'staff' : 'volunteer',
        profilePicture: '',
      });
      
      setIsAddingUser(false);
      toast.success('Invitation sent successfully (Development Mode)');
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleUpdateRole = (userId: string, newRole: string) => {
    try {
      // Find the staff member and update their role
      setStaff(prevStaff => 
        prevStaff.map(member => 
          member.id === userId 
            ? { ...member, role: newRole as BusinessRole | CharityRole } 
            : member
        )
      );
      
      toast.success('Role updated successfully (Development Mode)');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveUser = (userId: string) => {
    try {
      // Remove the staff member from the array
      setStaff(prevStaff => prevStaff.filter(member => member.id !== userId));
      toast.success('User removed successfully (Development Mode)');
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleResendInvitation = (_invitationId: string) => {
    try {
      toast.success('Invitation resent successfully (Development Mode)');
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleCancelInvitation = (invitationId: string) => {
    try {
      // Remove the invitation from the array
      setInvitations(prevInvitations => 
        prevInvitations.filter(invitation => invitation.id !== invitationId)
      );
      toast.success('Invitation cancelled successfully (Development Mode)');
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <UserGroupIcon className="h-6 w-6 mr-2 text-navy-blue" />
          {orgType === 'business' ? 'Business Staff Management' : 'Charity Staff Management'}
          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Dev Mode</span>
          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Owner Access</span>
        </h2>
        <button
          type="button"
          onClick={() => setIsAddingUser(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-blue"
        >
          <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Team Member
        </button>
      </div>

      {isAddingUser ? (
        <div className="bg-gray-50 p-4 rounded-md mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invite New Team Member</h3>
          <form onSubmit={sendInvitation}>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={newUser.name}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-navy-blue focus:border-navy-blue block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-navy-blue focus:border-navy-blue block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="john.doe@example.com"
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <div className="mt-1">
                  <select
                    id="role"
                    name="role"
                    value={newUser.role}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-navy-blue focus:border-navy-blue block w-full sm:text-sm border-gray-300 rounded-md"
                  >
                    {orgType === 'business' ? (
                      <>
                        <option value="staff">Staff</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="manager">Manager</option>
                        <option value="owner">Owner</option>
                      </>
                    ) : (
                      <>
                        <option value="volunteer">Volunteer</option>
                        <option value="staff">Staff</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="manager">Manager</option>
                        <option value="owner">Owner</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSendingInvite}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-navy-blue text-base font-medium text-white hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-blue sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isSendingInvite ? 'Sending...' : 'Send Invitation'}
              </button>
              <button
                type="button"
                onClick={() => setIsAddingUser(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-blue sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          {staff.length === 0 && invitations.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500">No team members have been added yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Active Staff Members */}
                  {staff.map((member) => (
                    <tr key={member.id} className="bg-white">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-100">
                            {member.profilePicture ? (
                              <img src={member.profilePicture} alt={member.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-navy-blue text-white text-lg font-medium">
                                {member.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">Since {new Date(member.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                          className="text-sm text-gray-900 border-gray-300 rounded-md shadow-sm focus:ring-navy-blue focus:border-navy-blue"
                        >
                          {orgType === 'business' ? (
                            <>
                              <option value="staff">Staff</option>
                              <option value="supervisor">Supervisor</option>
                              <option value="manager">Manager</option>
                              <option value="owner">Owner</option>
                            </>
                          ) : (
                            <>
                              <option value="volunteer">Volunteer</option>
                              <option value="staff">Staff</option>
                              <option value="supervisor">Supervisor</option>
                              <option value="manager">Manager</option>
                              <option value="owner">Owner</option>
                            </>
                          )}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to remove ${member.name}?`)) {
                              handleRemoveUser(member.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900 ml-4"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Pending Invitations */}
                  {invitations.length > 0 && (
                    <>
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pending Invitations
                        </td>
                      </tr>
                      {invitations.map((invite) => (
                        <tr key={invite.id} className="bg-white border-b border-gray-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">Pending Invitation</div>
                                <span className="text-xs text-gray-500">Sent {new Date(invite.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{invite.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleResendInvitation(invite.id)}
                              className="text-navy-blue hover:text-sky-blue mr-4"
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Cancel this invitation?')) {
                                  handleCancelInvitation(invite.id);
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DevStaffManagement;
